'use client';
import React, { useEffect, useRef, useState } from 'react';

interface Feature {
  icon: React.ReactNode;
  title: string;
  desc: string;
}

const features: Feature[] = [
{
  icon:
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" rx="12" fill="#E8F9FF" />
        <path d="M14 34V20l10-8 10 8v14H28v-8h-8v8H14z" stroke="#00AAE2" strokeWidth="2" strokeLinejoin="round" fill="none" />
        <path d="M10 22l14-11 14 11" stroke="#062543" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>,

  title: 'Optimized Civil Design',
  desc: 'We minimize civil construction costs through precision-engineered designs tailored to your specific site constraints and requirements.'
},
{
  icon:
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" rx="12" fill="#E8F9FF" />
        <circle cx="24" cy="24" r="10" stroke="#00AAE2" strokeWidth="2" fill="none" />
        <path d="M24 14v4M24 30v4M14 24h4M30 24h4" stroke="#062543" strokeWidth="2" strokeLinecap="round" />
        <circle cx="24" cy="24" r="3" fill="#00AAE2" />
      </svg>,

  title: 'Innovative Efficiency',
  desc: 'We continuously innovate to improve convenience, operational efficiency, and user experience across all our parking solutions.'
},
{
  icon:
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" rx="12" fill="#E8F9FF" />
        <rect x="12" y="28" width="8" height="8" rx="2" fill="#00AAE2" />
        <rect x="20" y="20" width="8" height="16" rx="2" fill="#062543" opacity="0.5" />
        <rect x="28" y="12" width="8" height="24" rx="2" fill="#062543" />
      </svg>,

  title: 'Maximized Capacity',
  desc: 'Multiply your parking capacity while keeping the overall cost per parking space low through intelligent space utilization.'
},
{
  icon:
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" rx="12" fill="#E8F9FF" />
        <path d="M16 32l4-8 4 4 4-8 4 4" stroke="#00AAE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <circle cx="24" cy="18" r="4" stroke="#062543" strokeWidth="2" fill="none" />
        <path d="M20 36h8" stroke="#062543" strokeWidth="2" strokeLinecap="round" />
      </svg>,

  title: 'Custom Engineering',
  desc: 'Every project is unique. We customize solutions to match your exact plot dimensions, vehicle count, and operational requirements.'
},
{
  icon:
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" rx="12" fill="#E8F9FF" />
        <path d="M24 12l3 7h7l-5.5 4.5 2 7L24 27l-6.5 3.5 2-7L14 19h7l3-7z" stroke="#00AAE2" strokeWidth="2" strokeLinejoin="round" fill="none" />
      </svg>,

  title: 'Precision Installation',
  desc: 'Our skilled installation team ensures every system is installed to perfection the first time, minimizing downtime and disruption.'
},
{
  icon:
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" rx="12" fill="#E8F9FF" />
        <path d="M24 14c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10" stroke="#00AAE2" strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M30 14l4 4-4 4" stroke="#062543" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M24 24v-4" stroke="#062543" strokeWidth="2" strokeLinecap="round" />
      </svg>,

  title: 'AMC & Maintenance',
  desc: 'Our dedicated maintenance team provides round-the-clock monitoring and Annual Maintenance Contracts for all installed systems.'
}];


export default function WhyChooseSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why-us"
      className="py-20 bg-white"
      style={{ fontFamily: 'Ubuntu, sans-serif' }}
      ref={ref}>
      
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Two-column layout like Tedra's innovation section */}
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Left: Image */}
          <div
            className={`w-full lg:w-5/12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`
            }>
            
            <div className="relative">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1e4fdd32e-1772792738039.png"
                alt="Bhargav Parking close-up detail of precision-engineered automated parking system mechanical components"
                className="w-full rounded-[20px] object-cover"
                style={{ aspectRatio: '4/5' }} />
              
              {/* Overlay card */}
              <div
                className="absolute top-6 right-2 md:-right-6 bg-[#00AAE2] text-white px-5 py-4 rounded-[16px] shadow-xl max-w-[180px]">
                
                <div className="text-[28px] font-bold" style={{ fontFamily: 'Ubuntu, sans-serif' }}>40+</div>
                <div className="text-[13px] text-white/90">Projects Completed</div>
              </div>
              <div
                className="absolute bottom-6 left-2 md:-left-6 bg-[#062543] text-white px-5 py-4 rounded-[16px] shadow-xl max-w-[180px]">
                
                <div className="text-[28px] font-bold text-[#00AAE2]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>2000+</div>
                <div className="text-[13px] text-white/80">Cars Parked</div>
              </div>
            </div>
          </div>

          {/* Right: Features */}
          <div
            className={`w-full lg:w-7/12 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`
            }>
            
            <p className="text-[#00AAE2] text-[15px] font-medium tracking-[2px] uppercase mb-3">Why Choose Us</p>
            <h2
              className="text-[#062543] font-medium mb-4"
              style={{ fontSize: '42px', lineHeight: '52px', fontFamily: 'Ubuntu, sans-serif' }}>
              
              Design & Innovation at Every Step
            </h2>
            <p
              className="text-[#3A3A3A] mb-10"
              style={{ fontSize: '17px', lineHeight: '28px', fontFamily: 'Ubuntu, sans-serif' }}>
              
              From initial concept to long-term maintenance, Bhargav Parking brings engineering excellence and innovation to every project we undertake.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((f, i) =>
              <div
                key={i}
                className={`flex gap-4 p-4 rounded-[16px] hover:bg-[#E8F9FF] transition-all duration-300 group ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`
                }
                style={{ transitionDelay: `${i * 80}ms` }}>
                
                  <div className="flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    {f.icon}
                  </div>
                  <div>
                    <h4
                    className="text-[#062543] font-medium mb-1"
                    style={{ fontSize: '17px', fontFamily: 'Ubuntu, sans-serif' }}>
                    
                      {f.title}
                    </h4>
                    <p
                    className="text-[#3A3A3A]"
                    style={{ fontSize: '14px', lineHeight: '22px', fontFamily: 'Ubuntu, sans-serif' }}>
                    
                      {f.desc}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}